import Phaser from 'phaser';
import { MUSIC_TRACK_KEYS, MUSIC_VOLUME, type MusicTrackKey } from '../constants/audio';
import { unlockAudioContext } from '../utils/audio';

// Кросс-сценовый source-of-truth для фоновой музыки. Singleton — переживает
// scene transitions (Menu/Map ↔ Game), чтобы трек не сбрасывался при смене
// сцены, и чтобы новый трек ВСЕГДА останавливал предыдущий (без наложения).
//
// Autoplay policy: WebAudio context на старте suspended (бразуер требует жест
// пользователя). Если playMenu/playCombat вызвали до жеста — менеджер ставит
// pending и стартует трек на phaser-событии 'unlocked' (эмитится при первом
// тапе/клике). На iOS m4a-фолбэк должен быть в массиве источников (см.
// constants/audio.ts MUSIC_SOURCES).

const FADE_DURATION_MS = 500;

export class MusicManager {
  private static instance: MusicManager;

  private currentTrack?: Phaser.Sound.BaseSound;
  private currentKey?: MusicTrackKey;
  // Желаемый трек, отложенный до разблокировки AudioContext первым жестом.
  private pendingKey?: MusicTrackKey;
  // Сцена, на которой подвешен pointerdown-слушатель — нужен для off() при
  // stop(), чтобы не висели мёртвые подписки. ОДНА сцена за раз: следующая
  // playX() с той же или другой сценой обновит pendingKey, но новый listener
  // не вешает, если уже подписан.
  private gestureSubscribedOn?: Phaser.Scene;
  private musicVolume = MUSIC_VOLUME;

  static getInstance(): MusicManager {
    if (!MusicManager.instance) {
      MusicManager.instance = new MusicManager();
    }
    return MusicManager.instance;
  }

  playMenu(scene: Phaser.Scene): void {
    this.requestPlay(scene, MUSIC_TRACK_KEYS.menu);
  }

  playCombat(scene: Phaser.Scene): void {
    this.requestPlay(scene, MUSIC_TRACK_KEYS.combat);
  }

  // Останавливает трек с фейдом и сбрасывает оба состояния (current + pending).
  // gesture-листенер НЕ снимаем явно: его обработчик САМ удалится после
  // срабатывания (once), а до срабатывания — pendingKey уже undefined,
  // обработчик увидит и ничего не запустит. Снос всех 'pointerdown' через
  // scene.input.off убил бы стрельбу/кнопки сцены — нельзя.
  // scene-параметр оставлен для совместимости вызовов — fadeOut на RAF
  // переживает scene transitions, scene не требуется.
  stop(_scene: Phaser.Scene): void {
    this.pendingKey = undefined;
    this.fadeOutCurrent();
    this.currentKey = undefined;
    this.currentTrack = undefined;
  }

  // Меняет громкость музыки. clamp 0..1. Если трек играет — применяется сразу,
  // иначе сохраняется для следующего play.
  setMusicVolume(v: number): void {
    this.musicVolume = Phaser.Math.Clamp(v, 0, 1);
    if (this.currentTrack?.isPlaying && 'setVolume' in this.currentTrack) {
      (this.currentTrack as Phaser.Sound.WebAudioSound).setVolume(this.musicVolume);
    }
  }

  // Внутренняя точка: тот же трек уже играет → ничего. Locked context →
  // запомнить и подождать первого жеста. Иначе — запустить с fade.
  private requestPlay(scene: Phaser.Scene, key: MusicTrackKey): void {
    if (this.currentKey === key && this.currentTrack?.isPlaying) return;

    if (scene.sound.locked) {
      this.pendingKey = key;
      this.subscribeGesture(scene);
      return;
    }

    this.startTrack(scene, key);
  }

  // Подписка на ПЕРВЫЙ pointerdown сцены. Phaser.Sound.Events.UNLOCKED
  // не всегда доставляется (зависит от bgWebAudio vs HTMLAudio, состояния
  // suspend, особенностей iOS), поэтому надёжнее использовать input gesture
  // напрямую: при первом тапе явно дёргаем unlockAudioContext (resume +
  // empty-buffer trick) и стартуем pending track.
  private subscribeGesture(scene: Phaser.Scene): void {
    if (this.gestureSubscribedOn === scene) return;
    this.gestureSubscribedOn = scene;
    scene.input.once('pointerdown', () => {
      this.gestureSubscribedOn = undefined;
      unlockAudioContext(scene);
      const key = this.pendingKey;
      this.pendingKey = undefined;
      if (key) this.startTrack(scene, key);
    });
  }

  private startTrack(scene: Phaser.Scene, key: MusicTrackKey): void {
    this.fadeOutCurrent();

    this.currentKey = key;
    const track = scene.sound.add(key, { loop: true, volume: 0 });
    this.currentTrack = track;
    track.play();

    this.fadeIn(track, this.musicVolume);
  }

  // Fade-in/out через requestAnimationFrame, НЕ scene.events.update. Сцена
  // может быть shutdown'ed в любой момент (scene.start между MapScene ↔ GameScene),
  // и тогда её update перестаёт срабатывать → fade застревает на промежуточном
  // volume, и зомби-трек остаётся играть. RAF глобальный, переживает transitions
  // и гарантированно дотягивает до stop()+destroy() за FADE_DURATION_MS.
  private fadeIn(track: Phaser.Sound.BaseSound, targetVolume: number): void {
    const startTime = performance.now();
    const tick = (): void => {
      if (!track.isPlaying) return;
      const elapsed = performance.now() - startTime;
      const progress = Math.min(1, elapsed / FADE_DURATION_MS);
      if ('setVolume' in track) {
        (track as Phaser.Sound.WebAudioSound).setVolume(targetVolume * progress);
      }
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  private fadeOutCurrent(): void {
    if (!this.currentTrack || !this.currentTrack.isPlaying) return;
    const trackToStop = this.currentTrack;
    const startVolume =
      (trackToStop as Phaser.Sound.WebAudioSound).volume ?? this.musicVolume;
    const startTime = performance.now();
    const tick = (): void => {
      if (!trackToStop.isPlaying) return;
      const elapsed = performance.now() - startTime;
      const progress = Math.min(1, elapsed / FADE_DURATION_MS);
      if ('setVolume' in trackToStop) {
        (trackToStop as Phaser.Sound.WebAudioSound).setVolume(startVolume * (1 - progress));
      }
      if (progress >= 1) {
        trackToStop.stop();
        trackToStop.destroy();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
}
